import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, Tag, Tooltip } from 'antd';
import RestHelper from 'utils/RestHelper';
import { decodeCsSdkEncodedValue } from 'utils/helper';
import { convertIosModelToProductName } from 'utils/model-convert/ios-model-convert';
import {
  isAndroid,
  isAndroidOrHarmony,
  isIos,
  isLinux,
  isMac,
  isMobile,
  isPcOrLinux,
  isWechatMiniProgram,
} from 'utils/platform';
import { VmTypeInt } from 'utils/constants/vm-type';
import { ze } from 'utils/zhEn';

const LIFT_THRESHOLD = 1.8;
const LIFT_MIN_ISSUE_RATIO = 0.15;

function normalizeKey(str) {
  return String(str || '').trim().toLowerCase();
}

export function isTrivialConcentration(fieldKey, valueName, rawKey) {
  const unknownTopValue = /^(未知|unknown|n\/a|null|none|空|—|-)$/;
  const names = [normalizeKey(valueName), normalizeKey(rawKey)];
  if (names.every((n) => !n) || names.some((n) => unknownTopValue.test(n))) {
    return true;
  }
  const hit = (re) => names.some((n) => re.test(n));
  if (fieldKey === 'vmType') return hit(/^(真机|physical device|1)$/);
  if (fieldKey === 'isRooted' || fieldKey === 'crashDataType') return hit(/^(否|no|false)$/);
  if (fieldKey === 'cpuType') return hit(/^(arm64-v8a|arm64)$/);
  if (fieldKey === 'appBit') return hit(/^(64位|64 bit|64)$/);
  if (fieldKey === 'totalGpuMemGib' || fieldKey === 'ram') return hit(/^(0|0 gib|0gib)$/);
  if (
    fieldKey === 'reservedMapFlattened.startupCrash' ||
    fieldKey === 'reservedMapFlattened.startupHang'
  ) {
    return hit(/^(否|no|false)$/);
  }
  return false;
}

export function isBlankPieSlice(fieldKey, valueName, rawKey) {
  const names = [normalizeKey(valueName), normalizeKey(rawKey)];
  if (names.every((n) => !n)) return true;
  if (fieldKey === 'ram' || fieldKey === 'totalGpuMemGib') {
    return names.some((n) => /^(0|0 gib|0gib)$/.test(n));
  }
  return false;
}

export function buildBaselineRatioMap(rows = []) {
  const map = {};
  for (const row of rows || []) {
    const total = Number(row.fieldRollupAccessDevices || 0);
    const access = Number(row.accessDevices || 0);
    if (total <= 0 || access <= 0) continue;
    const ratio = access / total;
    for (const k of [row.fieldOriginalValue, row.fieldValue]) {
      const nk = normalizeKey(k);
      if (nk) {
        map[nk] = ratio;
      }
    }
  }
  return map;
}

export function wilsonLowerBound(k, n, z = 1.96) {
  const count = Number(k || 0);
  const total = Number(n || 0);
  if (total <= 0 || count <= 0) return 0;
  const phat = Math.min(1, count / total);
  const z2 = z * z;
  const denom = 1 + z2 / total;
  const center = phat + z2 / (2 * total);
  const half = z * Math.sqrt((phat * (1 - phat) + z2 / (4 * total)) / total);
  return Math.max(0, (center - half) / denom);
}

export function resolveBaselineQueryContext(searchConditionGroup) {
  let minDate = moment().subtract(6, 'days').format('YYYYMMDD');
  let maxDate = moment().format('YYYYMMDD');
  let version = '-1';
  let versionList;
  let mergeMultipleVersionsWithInaccurateResult = false;

  const conditions = searchConditionGroup?.conditions || [];
  const timeCond = conditions.find(
    (c) => c && (c.field === 'crashUploadTime' || c.field === 'crashTime' || c.field === 'uploadTime')
  );
  if (timeCond) {
    if (timeCond.queryType === 'RANGE_RELATIVE_DATETIME' && Number(timeCond.gte) > 0) {
      const days = Math.max(0, Math.ceil(Number(timeCond.gte) / (86400 * 1000)) - 1);
      minDate = moment().subtract(days, 'days').format('YYYYMMDD');
      maxDate = moment().format('YYYYMMDD');
    } else if (timeCond.queryType === 'RANGE' && (timeCond.gte || timeCond.lte)) {
      if (timeCond.gte) minDate = moment(timeCond.gte).format('YYYYMMDD');
      if (timeCond.lte) maxDate = moment(timeCond.lte).format('YYYYMMDD');
    }
  }

  const verCond = conditions.find((c) => c && c.field === 'version' && !c.not);
  if (verCond) {
    if (Array.isArray(verCond.terms) && verCond.terms.length > 0) {
      const clean = verCond.terms.map((v) => String(v || '').trim()).filter(Boolean);
      if (clean.length === 1) {
        version = clean[0];
      } else if (clean.length > 1) {
        version = undefined;
        versionList = clean;
        mergeMultipleVersionsWithInaccurateResult = true;
      }
    } else if (verCond.term && String(verCond.term).trim()) {
      version = String(verCond.term).trim();
    }
  }

  return {
    minDate,
    maxDate,
    mergeMultipleDatesWithInaccurateResult: minDate !== maxDate,
    version,
    versionList,
    mergeMultipleVersionsWithInaccurateResult,
  };
}

// 偏斜只认相对本应用大盘的 Lift。没有基线时，占比再高也不叫集中。
export function evaluateDimensionAnomaly({
  fieldKey,
  fieldLabel,
  total,
  noDataCount = 0,
  valueList = [],
  baselineMap = {},
}) {
  const minValidSample = 20;
  const minBucketCount = 10;
  const minLiftLowerBound = 1.4;
  if (!Array.isArray(valueList) || valueList.length === 0) {
    return null;
  }
  const blankBucketsCount = valueList.reduce((acc, b) => {
    if (!b || b.isNoData || b.isShowAsOthers || b.notInLimitedBuckets) return acc;
    const rawKey = decodeCsSdkEncodedValue(b.key || b.name || '');
    return isBlankPieSlice(fieldKey, b.name || rawKey, rawKey)
      ? acc + Number(b.count || 0)
      : acc;
  }, 0);
  const validTotal = Math.max(
    0,
    Number(total || 0) - Number(noDataCount || 0) - blankBucketsCount
  );
  if (validTotal <= 0) {
    return null;
  }
  if (validTotal < minValidSample) {
    return {
      kind: 'normal',
      fieldKey,
      fieldLabel,
      valueName: '',
      issueRatio: 0,
      baselineRatio: null,
      lift: null,
      score: 0,
    };
  }
  const realBuckets = valueList.filter((b) => {
    if (
      !b ||
      b.isNoData ||
      b.isShowAsOthers ||
      b.notInLimitedBuckets ||
      Number(b.count || 0) <= 0
    ) {
      return false;
    }
    const rawKey = decodeCsSdkEncodedValue(b.key || b.name || '');
    return !isBlankPieSlice(fieldKey, b.name || rawKey, rawKey);
  });
  if (realBuckets.length === 0) {
    return null;
  }

  let bestLift = null;
  for (const b of realBuckets) {
    const count = Number(b.count || 0);
    if (count < minBucketCount) continue;
    const issueRatio = count / validTotal;
    if (issueRatio < LIFT_MIN_ISSUE_RATIO) continue;
    const rawKey = decodeCsSdkEncodedValue(b.key || b.name || '');
    if (isTrivialConcentration(fieldKey, b.name || rawKey, rawKey)) continue;
    const baseRatio =
      baselineMap[normalizeKey(rawKey)] ??
      baselineMap[normalizeKey(b.name)] ??
      null;
    if (baseRatio && baseRatio > 0.002) {
      const lift = issueRatio / baseRatio;
      const lowerRatio = wilsonLowerBound(count, validTotal);
      const liftLowerBound = lowerRatio / baseRatio;
      if (
        lift >= LIFT_THRESHOLD &&
        liftLowerBound >= minLiftLowerBound &&
        (!bestLift || lift > bestLift.lift)
      ) {
        bestLift = {
          kind: 'lift',
          fieldKey,
          fieldLabel,
          valueName: b.name || rawKey,
          issueRatio,
          baselineRatio: baseRatio,
          lift,
          liftLowerBound,
          score: lift * 25 + issueRatio * 100,
        };
      }
    }
  }

  let topBaselineComparison = null;
  const topBucket = realBuckets[0];
  if (topBucket) {
    const topCount = Number(topBucket.count || 0);
    const topIssueRatio = topCount / validTotal;
    const topRawKey = decodeCsSdkEncodedValue(topBucket.key || topBucket.name || '');
    const topBaseRatio =
      baselineMap[normalizeKey(topRawKey)] ??
      baselineMap[normalizeKey(topBucket.name)] ??
      null;
    if (
      topCount >= minBucketCount &&
      topIssueRatio >= 0.3 &&
      !isTrivialConcentration(fieldKey, topBucket.name || topRawKey, topRawKey) &&
      topBaseRatio &&
      topBaseRatio > 0.002
    ) {
      const topLift = topIssueRatio / topBaseRatio;
      if (topLift < LIFT_THRESHOLD) {
        topBaselineComparison = {
          fieldKey,
          fieldLabel,
          valueName: topBucket.name || topRawKey,
          issueRatio: topIssueRatio,
          baselineRatio: topBaseRatio,
          lift: topLift,
        };
      }
    }
  }

  if (bestLift) {
    return {
      ...bestLift,
      topBaselineComparison,
    };
  }
  return {
    kind: 'normal',
    fieldKey,
    fieldLabel,
    valueName: topBaselineComparison ? topBaselineComparison.valueName : '',
    issueRatio: topBaselineComparison ? topBaselineComparison.issueRatio : 0,
    baselineRatio: topBaselineComparison ? topBaselineComparison.baselineRatio : null,
    lift: topBaselineComparison ? topBaselineComparison.lift : null,
    topBaselineComparison,
    score: 0,
  };
}

export function getOutlierCustomFieldsByPlatform(pid, appId, includeAndroidGpu = true) {
  const linux = isLinux(pid, appId);
  const pcHardware = linux || (Number(pid) === 10 && !linux);
  const mac = isMac(pid);
  const ios = isIos(pid);
  const mobile = isMobile(pid);
  const android = isAndroid(pid);
  const androidOrHarmony = isAndroidOrHarmony(pid);
  const yesNo = {
    true: ze('是', 'Yes'),
    false: ze('否', 'No'),
  };

  const list = [
    {
      name: isPcOrLinux(pid) ? 'osName' : 'os',
      label: ze('系统版本', 'OS Version'),
      aggregateType: 0,
      baselineField: 'osVersion',
    },
    {
      name: 'gpu',
      label: 'GPU',
      aggregateType: 0,
      baselineField: 'gpu',
      available: (android && includeAndroidGpu) || pcHardware,
    },
  ];

  if (pcHardware || mac) {
    list.push({
      name: 'cpu',
      label: 'CPU',
      aggregateType: 0,
      baselineField: 'cpu',
    });
  }
  if (pcHardware) {
    list.push(
      {
        name: 'displayVersion',
        label: ze('显卡驱动版本', 'GPU Driver'),
        aggregateType: 0,
        baselineField: 'gpuDriverVersion',
      },
      {
        name: 'totalGpuMemGib',
        label: ze('GPU内存', 'GPU Memory'),
        aggregateType: 0,
        formatValue: (k) => `${Number(k)} GiB`,
      }
    );
  }
  if (android) {
    list.push(
      { name: 'reservedMapFlattened.soc', label: 'SoC', aggregateType: 0 },
      { name: 'brand', label: ze('厂商', 'Brand'), aggregateType: 0 }
    );
  }
  if (androidOrHarmony) {
    list.push({
      name: 'vmType',
      label: ze('运行环境', 'Environment'),
      aggregateType: 0,
      bucketKeyDict: {
        [VmTypeInt.ALL]: ze('未知', 'Unknown'),
        [VmTypeInt.REAL_DEVICE]: ze('真机', 'Physical Device'),
        [VmTypeInt.EMULATOR]: ze('模拟器/云游戏', 'Emulator / Cloud'),
        [VmTypeInt.CLOUD_GAMING]: ze('云游戏', 'Cloud Gaming'),
      },
    });
  }
  if (ios) {
    list.push(
      {
        name: 'reservedMapFlattened.startupCrash',
        label: ze('是否启动崩溃', 'Startup Crash'),
        aggregateType: 0,
        bucketKeyDict: yesNo,
      },
      {
        name: 'reservedMapFlattened.startupHang',
        label: ze('是否是启动卡死', 'Startup Hang'),
        aggregateType: 0,
        bucketKeyDict: yesNo,
      }
    );
  }
  if (mobile || isWechatMiniProgram(pid)) {
    list.push({
      name: 'hardware',
      label: ze('机型设备', 'Device Model'),
      aggregateType: 0,
      baselineField: 'model',
    });
  }
  if (mobile) {
    list.push({
      name: 'ram',
      label: ze('内存容量', 'RAM'),
      aggregateType: 0,
      formatValue: (k) => `${Number(k)} GiB`,
    });
  }
  return list.filter((x) => x.available !== false);
}

const IssueOutlierBar = ({ appId, pid, issueId, includeAndroidGpu = true, onJumpToDeviceStats }) => {
  const [loading, setLoading] = useState(false);
  const [outliers, setOutliers] = useState([]);
  const [topBaselineItem, setTopBaselineItem] = useState(null);
  const [hasValidSample, setHasValidSample] = useState(false);

  useEffect(() => {
    if (!appId || !issueId) return undefined;
    let cancelled = false;
    setLoading(true);
    setOutliers([]);
    setTopBaselineItem(null);
    setHasValidSample(false);

    const minDate = moment().subtract(6, 'days').format('YYYYMMDD');
    const maxDate = moment().format('YYYYMMDD');
    const customFields = getOutlierCustomFieldsByPlatform(pid, appId, includeAndroidGpu);
    const baselineFields = Array.from(
      new Set(customFields.map((f) => f.baselineField).filter(Boolean))
    );

    const fetchBaseline = async (field) => {
      try {
        const rsp = await RestHelper.post('/redir/api/statResource/fetchFieldTopStats', {
          appId,
          pid: Number(pid) || 1,
          platformId: Number(pid) || 1,
          mergeMultipleVersionsWithInaccurateResult: false,
          version: '-1',
          minDate,
          maxDate,
          mergeMultipleDatesWithInaccurateResult: true,
          type: 'crash',
          limit: 30,
          field,
          sortByException: false,
          countryList: [],
          vmTypeList: [0],
          needQueryFieldRollup: true,
        });
        return buildBaselineRatioMap(rsp?.json?.data || []);
      } catch (e) {
        return {};
      }
    };

    (async () => {
      try {
        const [aggRsp, ...baseMaps] = await Promise.all([
          RestHelper.post('/api/issue/queryIssueAggregate', {
            appId,
            platformId: Number(pid) || 1,
            issueHash: issueId,
            customFields: customFields.map(({ name, aggregateType }) => ({ name, aggregateType })),
            searchConditionGroup: {
              conditions: [
                {
                  field: 'crashUploadTime',
                  queryType: 'RANGE_RELATIVE_DATETIME',
                  gte: 7 * 86400 * 1000,
                },
              ],
            },
          }),
          ...baselineFields.map((bf) => fetchBaseline(bf)),
        ]);

        if (cancelled) return;
        const baselineByField = {};
        baselineFields.forEach((bf, i) => {
          baselineByField[bf] = baseMaps[i] || {};
        });

        const rawList = aggRsp?.json?.data || [];
        const detected = [];
        const baselineCandidates = [];
        let anySample = false;

        rawList.forEach((item, idx) => {
          const meta = customFields[idx];
          if (!meta || !item) return;
          const allPositiveBuckets = (item.bucketList || []).filter(
            (b) => Number(b.docCount || 0) > 0
          );
          const withData = allPositiveBuckets.reduce((acc, b) => acc + Number(b.docCount || 0), 0);
          if (withData > 0) {
            anySample = true;
          }
          const buckets = allPositiveBuckets
            .filter((b) => !b.notInLimitedBuckets)
            .sort((a, b) => b.docCount - a.docCount);
          const valueList = buckets.map((b) => {
            const decoded = decodeCsSdkEncodedValue(b.key);
            let mapped = decoded;
            if (meta.bucketKeyDict) {
              mapped = meta.bucketKeyDict[b.key] || decoded;
            } else if (meta.formatValue) {
              mapped = meta.formatValue(decoded);
            } else if (meta.name === 'hardware') {
              mapped = convertIosModelToProductName(decoded);
            }
            return {
              key: b.key,
              name: mapped,
              count: Number(b.docCount || 0),
            };
          });
          const baseMap = meta.baselineField ? baselineByField[meta.baselineField] || {} : {};
          const res = evaluateDimensionAnomaly({
            fieldKey: meta.name,
            fieldLabel: meta.label,
            total: withData,
            noDataCount: 0,
            valueList,
            baselineMap: baseMap,
          });
          if (res && res.kind === 'lift') {
            detected.push(res);
          }
          if (
            res &&
            res.topBaselineComparison &&
            res.topBaselineComparison.issueRatio >= 0.35 &&
            res.topBaselineComparison.baselineRatio != null
          ) {
            baselineCandidates.push(res.topBaselineComparison);
          }
        });

        detected.sort((a, b) => b.score - a.score);
        baselineCandidates.sort((a, b) => b.issueRatio - a.issueRatio);
        setHasValidSample(anySample);
        setOutliers(detected.slice(0, 4));
        setTopBaselineItem(baselineCandidates[0] || null);
      } catch (e) {
        if (!cancelled) {
          setOutliers([]);
          setTopBaselineItem(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [appId, pid, issueId, includeAndroidGpu]);

  if (loading || !hasValidSample) {
    return null;
  }

  return (
    <div
      data-testid="issue-outlier-bar"
      style={{
        marginTop: '10px',
        padding: '8px 12px',
        background: outliers.length > 0 ? '#fffbe6' : '#fafafa',
        border: outliers.length > 0 ? '1px solid #ffe58f' : '1px solid #f0f0f0',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
        <span
          style={{
            fontWeight: 600,
            fontSize: '12px',
            color: outliers.length > 0 ? '#d46b08' : '#389e0d',
          }}
        >
          {outliers.length > 0
            ? `${ze('⚡ 异常维度偏斜 (近7天)', '⚡ Dimension Outliers (Last 7d)')}:`
            : `${ze('✓ 环境分布与本应用大盘基本一致 (近7天)', '✓ Environment Matches App Baseline (Last 7d)')}:`}
        </span>
        {outliers.length === 0 ? (
          <span style={{ fontSize: '12px', color: '#595959' }}>
            {topBaselineItem
              ? ze(
                  `未见显著高于大盘的特征（最高项 ${topBaselineItem.fieldLabel} ${topBaselineItem.valueName} 本问题占 ${(topBaselineItem.issueRatio * 100).toFixed(1)}%，应用大盘同为 ${(topBaselineItem.baselineRatio * 100).toFixed(1)}% · ${topBaselineItem.lift.toFixed(1)}x）`,
                  `No facet significantly exceeds baseline (top item ${topBaselineItem.fieldLabel} ${topBaselineItem.valueName} is ${(topBaselineItem.issueRatio * 100).toFixed(1)}% vs app baseline ${(topBaselineItem.baselineRatio * 100).toFixed(1)}% · ${topBaselineItem.lift.toFixed(1)}x)`
                )
              : ze(
                  '未见显著高于应用大盘的设备或系统版本特征',
                  'No device or OS dimension significantly exceeds app baseline'
                )}
          </span>
        ) : (
          outliers.map((item) => {
            if (item.kind !== 'lift') return null;
            const issuePct = `${(item.issueRatio * 100).toFixed(1)}%`;
            const basePct = `${(item.baselineRatio * 100).toFixed(1)}%`;
            const liftStr = `${item.lift.toFixed(1)}x`;
            return (
              <Tooltip
                key={item.fieldKey}
                title={ze(
                  `本问题异常次数占比 ${issuePct} vs 近7天大盘联网设备占比 ${basePct}（偏斜 ${liftStr}）`,
                  `Issue report share ${issuePct} vs 7d baseline device share ${basePct} (${liftStr} skew)`
                )}
              >
                <Tag color="volcano" style={{ margin: 0, fontWeight: 500 }}>
                  {`${item.fieldLabel}: ${item.valueName} (${issuePct} vs ${ze('大盘', 'Base')} ${basePct} · ${liftStr})`}
                </Tag>
              </Tooltip>
            );
          })
        )}
      </div>
      {onJumpToDeviceStats && (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, fontSize: '12px' }}
          onClick={onJumpToDeviceStats}
        >
          {ze('查看设备分布 →', 'View Device Distribution →')}
        </Button>
      )}
    </div>
  );
};

IssueOutlierBar.propTypes = {
  appId: PropTypes.string,
  pid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  issueId: PropTypes.string,
  includeAndroidGpu: PropTypes.bool,
  onJumpToDeviceStats: PropTypes.func,
};

export default IssueOutlierBar;
